import { useState, useEffect } from 'react';
import { DollarSign, CheckCircle, Clock, XCircle } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const PurchasesManager = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      const response = await adminApi.getPurchases();
      setPurchases(response.data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'pending':
      case 'unpaid':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      default:
        return <XCircle className="w-4 h-4 text-red-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return 'badge-success';
      case 'pending':
      case 'unpaid':
        return 'badge-pending';
      default:
        return 'badge-error';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const totalRevenue = purchases
    .filter(p => p.payment_status === 'paid')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const completedCount = purchases.filter(p => p.payment_status === 'paid').length;
  const pendingCount = purchases.filter(p => p.payment_status === 'unpaid' || p.payment_status === 'pending').length;

  return (
    <div data-testid="purchases-manager">
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold mb-2">Purchases</h1>
        <p className="text-muted-foreground">Track completed transactions.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="admin-stat-card">
          <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-4">
            <DollarSign className="w-6 h-6 text-green-400" />
          </div>
          <p className="text-3xl font-heading font-bold mb-1">${totalRevenue.toLocaleString()}</p>
          <p className="text-muted-foreground text-sm">Total Revenue</p>
        </div>
        <div className="admin-stat-card">
          <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center mb-4">
            <CheckCircle className="w-6 h-6 text-primary" />
          </div>
          <p className="text-3xl font-heading font-bold mb-1">{completedCount}</p>
          <p className="text-muted-foreground text-sm">Completed</p>
        </div>
        <div className="admin-stat-card">
          <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center mb-4">
            <Clock className="w-6 h-6 text-yellow-400" />
          </div>
          <p className="text-3xl font-heading font-bold mb-1">{pendingCount}</p>
          <p className="text-muted-foreground text-sm">Pending</p>
        </div>
      </div>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Package</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Session ID</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((purchase) => (
              <tr key={purchase.id} data-testid={`purchase-row-${purchase.id}`}>
                <td className="font-medium">{purchase.package_name}</td>
                <td className="text-primary font-mono">
                  ${purchase.amount?.toLocaleString()} {purchase.currency?.toUpperCase()}
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(purchase.payment_status)}
                    <span className={getStatusBadge(purchase.payment_status)}>
                      {purchase.payment_status}
                    </span>
                  </div>
                </td>
                <td className="text-muted-foreground text-xs font-mono truncate max-w-[150px]">
                  {purchase.session_id}
                </td>
                <td className="text-muted-foreground text-sm">{formatDate(purchase.created_at)}</td>
              </tr>
            ))}
            {purchases.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted-foreground py-8">
                  No purchases yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PurchasesManager;
